import React, { startTransition, useMemo, useState } from 'react';
import {
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import { Academy } from '../../hooks/api/academy';
import { Booking, useGetBookings } from '../../hooks/api/booking';
import { Lesson, useGetLessons } from '../../hooks/api/lesson';
import { useGetLessonScheduleRules } from '../../hooks/api/lessonScheduleRule';
import {
  addDaysToDate,
  convertDateToDateString,
  getStartOfWeek,
  parseDateInputValue,
} from '../../util/dates';
import InstructorLessonDetailsDialog from './InstructorLessonDetailsDialog';
import InstructorLessonsHeader from './InstructorLessonsHeader';
import InstructorScheduleTemplateEditor from './InstructorScheduleTemplateEditor';
import InstructorWeeklySchedule from './InstructorWeeklySchedule';

interface InstructorDesktopLessonsViewProps {
  academies: Academy[];
  academyId: number;
  date: string;
  onAcademyChange: (academyId: number) => void;
  onCreateLesson: () => void;
  onDateChange: (date: string) => void;
}

const EMPTY_BOOKINGS: Booking[] = [];
const EMPTY_LESSONS: Lesson[] = [];
export type InstructorLessonsViewMode = 'generated' | 'template';

interface ViewModeSwitcherProps {
  viewMode: InstructorLessonsViewMode;
  onViewModeChange: (viewMode: InstructorLessonsViewMode) => void;
}

function ViewModeSwitcher({ viewMode, onViewModeChange }: ViewModeSwitcherProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        mb: 1.25,
        borderRadius: 4,
        border: '1px solid rgba(18,18,18,0.08)',
        backgroundColor: 'white',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Typography
          variant="body2"
          sx={{ color: 'rgba(17,17,17,0.56)', fontWeight: 700, px: 1 }}
        >
          Vista de planificación
        </Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={viewMode}
          onChange={(_, selectedViewMode: InstructorLessonsViewMode | null) => {
            if (selectedViewMode) {
              onViewModeChange(selectedViewMode);
            }
          }}
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.75,
              py: 0.75,
              borderColor: 'rgba(18,18,18,0.12)',
              color: '#111111',
              textTransform: 'none',
              fontWeight: 800,
              gap: 0.75,
              '&.Mui-selected': {
                bgcolor: '#111111',
                color: 'white',
                '&:hover': {
                  bgcolor: '#222222',
                },
              },
            },
          }}
        >
          <ToggleButton value="generated" aria-label="Clases generadas">
            <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />
            Semana
          </ToggleButton>
          <ToggleButton value="template" aria-label="Plantilla semanal">
            <EventRepeatRoundedIcon sx={{ fontSize: 18 }} />
            Plantilla
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Paper>
  );
}

export default function InstructorDesktopLessonsView({
  academies,
  academyId,
  date,
  onAcademyChange,
  onCreateLesson,
  onDateChange,
}: InstructorDesktopLessonsViewProps) {
  const selectedDate = useMemo(() => parseDateInputValue(date), [date]);
  const weekStartDate = useMemo(() => getStartOfWeek(selectedDate), [selectedDate]);
  const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<InstructorLessonsViewMode>('generated');
  const [scheduleRuleCreateRequestKey, setScheduleRuleCreateRequestKey] = useState(0);

  const moveWeek = (days: number) => {
    startTransition(() => {
      setSelectedLessonId(null);
      onDateChange(convertDateToDateString(addDaysToDate(selectedDate, days)));
    });
  };

  const goToCurrentWeek = () => {
    startTransition(() => {
      setSelectedLessonId(null);
      onDateChange(convertDateToDateString(new Date()));
    });
  };

  const weeklyLessonsQuery = useGetLessons({ weekStartDate, academyId });
  const bookingsQuery = useGetBookings({ weekStartDate });
  const scheduleRulesQuery = useGetLessonScheduleRules({ active: true, academyId });

  const weeklyLessons = weeklyLessonsQuery.data ?? EMPTY_LESSONS;
  const bookings = bookingsQuery.data ?? EMPTY_BOOKINGS;
  const scheduleRules = scheduleRulesQuery.data ?? [];
  const bookingsByLessonId = useMemo(() => {
    const lessonBookingsMap = new Map<number, Booking[]>();

    bookings.forEach((booking) => {
      const lessonBookings = lessonBookingsMap.get(booking.lessonId);
      if (lessonBookings) {
        lessonBookings.push(booking);
        return;
      }

      lessonBookingsMap.set(booking.lessonId, [booking]);
    });

    return lessonBookingsMap;
  }, [bookings]);
  const lessonsCount = weeklyLessons.length;
  const bookingsCount = bookings.length;
  const totalSeats = weeklyLessons.reduce((sum, lesson) => sum + lesson.maxSeats, 0);
  const occupancy = totalSeats === 0 ? 0 : Math.round((bookingsCount / totalSeats) * 100);
  const templateLessonsCount = scheduleRules.length;
  const templateSeats = scheduleRules.reduce((sum, rule) => sum + rule.maxSeats, 0);
  const selectedLesson = useMemo(() => {
    if (selectedLessonId === null) {
      return undefined;
    }

    return weeklyLessons.find((lesson) => lesson.id === selectedLessonId);
  }, [selectedLessonId, weeklyLessons]);
  const selectedLessonBookings = useMemo(() => {
    if (!selectedLesson) {
      return [];
    }

    return bookingsByLessonId.get(selectedLesson.id) ?? [];
  }, [bookingsByLessonId, selectedLesson]);

  return (
    <>
      <ViewModeSwitcher viewMode={viewMode} onViewModeChange={setViewMode} />

      <InstructorLessonsHeader
        academies={academies}
        academyId={academyId}
        date={date}
        lessonsCount={lessonsCount}
        occupancy={occupancy}
        templateLessonsCount={templateLessonsCount}
        templateSeats={templateSeats}
        totalSeats={totalSeats}
        viewMode={viewMode}
        onAcademyChange={onAcademyChange}
        onCreateLesson={
          viewMode === 'generated'
            ? onCreateLesson
            : () => setScheduleRuleCreateRequestKey((currentValue) => currentValue + 1)
        }
        onDateChange={onDateChange}
      />

      {viewMode === 'generated' ? (
        <InstructorWeeklySchedule
          activeLessonId={selectedLessonId ?? undefined}
          bookingsByLessonId={bookingsByLessonId}
          currentDate={selectedDate}
          lessons={weeklyLessons}
          onLessonSelect={setSelectedLessonId}
          onNextWeek={() => moveWeek(7)}
          onPreviousWeek={() => moveWeek(-7)}
          onToday={goToCurrentWeek}
          weekStartDate={weekStartDate}
        />
      ) : (
        <InstructorScheduleTemplateEditor
          academies={academies}
          academyId={academyId}
          createRequestKey={scheduleRuleCreateRequestKey}
          scheduleRules={scheduleRules}
        />
      )}

      <InstructorLessonDetailsDialog
        open={viewMode === 'generated' && selectedLesson !== undefined}
        lesson={selectedLesson}
        bookings={selectedLessonBookings}
        onClose={() => setSelectedLessonId(null)}
      />
    </>
  );
}
